<template>
    <div>
        <!-- filter -->
        <v-form @submit.prevent="isFiltered = true; needToResetShownRows = true; fetchVisitCount()">
            <v-row class="mx-1 my-1">
                <!-- city region filter -->
                <v-col md="5" sm="7" cols="12" class="pa-1 d-flex justify-space-between">
                    <v-row class="pa-3">
                        <!-- city name -->
                        <v-col sm="5" cols="12" class="pa-0 pe-1 d-flex">
                            <v-autocomplete
                                v-model="cityId"
                                :items="cities"
                                item-text="name"
                                item-value="id"
                                :return-object="false"
                                :label="$t('inputs.city')"
                                dense
                                hide-details
                                append-icon
                                :loading="filterLoading"
                                :disabled="loading"
                                @change="fetchRegionsDueCity(); searchCityId = null"
                                outlined
                                prepend-icon="mdi-map-marker-multiple"
                                multiple
                                :search-input.sync="searchCityId"
                            >
                                <template v-slot:prepend-item>
                                    <v-list-item
                                        ripple
                                        @click="toggleCities"
                                    >
                                        <v-list-item-action>
                                            <v-icon>
                                                {{ iconCities }}
                                            </v-icon>
                                        </v-list-item-action>
                                        
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{$t('select-all')}}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-divider class="mt-2"></v-divider>
                                </template>

                                <template v-slot:selection="{ item, index }">
                                    <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                                    <span
                                        v-if="index === 1"
                                        class="grey--text text-caption"
                                    >
                                        (+{{ cityId.length - 1 }})
                                    </span>
                                </template>
                            </v-autocomplete>
                        </v-col>
            
                        <!-- region -->
                        <v-col sm="7" cols="12" class="pa-0">
                            <v-autocomplete
                                v-model="regionIds"
                                :items="regionsAll.filter(c => cityId.includes(c.cityId))"
                                item-text="name"
                                item-value="id"
                                :return-object="false"
                                :label="managementHierarchyRegions ? $t('inputs.areas') : $t('inputs.region')"
                                dense
                                multiple
                                clearable
                                hide-details
                                :loading="regionsLoading"
                                :disabled="loading || regionsLoading || cityId === []"
                                outlined
                                :search-input.sync="searchRegion"
                                @change="searchRegion = null"
                                append-outer-icon="mdi-select-search"
                                @click:append-outer="getMedical()"
                            >
                                <template v-slot:prepend-item v-if="regions">
                                    <v-list-item
                                        ripple
                                        @click="toggle"
                                    >
                                        <v-list-item-action>
                                            <v-icon>
                                                {{ icon }}
                                            </v-icon>
                                        </v-list-item-action>
                                        
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ $t('select-all') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-divider class="mt-2"></v-divider>
                                </template>
            
                                <template v-slot:selection="{ item, index }">
                                    <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                                    <span
                                        v-if="index === 1"
                                        class="grey--text text-caption"
                                    >
                                        (+{{ regionIds.length - 1 }})
                                    </span>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-col>
            
                <!-- date -->
                <!-- start date -->
                <v-col md="2" sm="4" cols="12" class="pa-1 ms-md-7">
                    <bee-date-picker
                        v-model="startDate"
                        :input-label="$t('inputs.start-date')"
                        elevation="2"
                        menu-picker
                        dense
                        hide-details
                        :input-props="{
                            outlined: true,
                            dense: true,
                            clearable: true,
                        }"
                        :disabled="loading"
                        :allowed-dates="disableDates"
                    ></bee-date-picker>
                </v-col>
            
                <!-- end date -->
                <v-col md="2" sm="4" cols="12" class="pa-1">
                    <bee-date-picker
                        v-model="endDate"
                        :input-label="$t('inputs.end-date')"
                        elevation="2"
                        menu-picker
                        dense
                        hide-details
                        :input-props="{
                            outlined: true,
                            dense: true,
                            clearable: true,
                        }"
                        :disabled="loading" :allowed-dates="((date) => {
                            const day = new Date(date).getDay();
                            if(isDisabledFriday) {
                                return day !== 5
                            } else {
                                return day !== -1
                            }
                        })"
                    ></bee-date-picker>
                </v-col>

                <!-- doctor name -->
                <v-col md="2" sm="6" cols="12" class="pa-1 d-flex justify-start">
                    <v-text-field
                        dense
                        v-model="doctorName"
                        :label="$t('inputs.doctor')"
                        hide-details
                        clearable
                        :disabled="loading"
                        outlined
                        prepend-icon="mdi-doctor"
                    ></v-text-field>
                </v-col>

                <!-- classification -->
                <v-col md="2" sm="6" cols="12" class="pa-1">
                    <v-autocomplete
                        v-model="workload"
                        :items="classification"
                        item-value="id"
                        item-text="name"
                        :return-object="false"
                        :label="$t('inputs.class')"
                        dense
                        clearable
                        hide-details
                        :disabled="loading"
                        outlined
                        multiple
                    ></v-autocomplete>
                </v-col>

                <!-- specialty -->
                <v-col md="3" sm="6" cols="12" class="pa-1">
                    <v-autocomplete
                        v-model="specialtyIds"
                        :items="specialties"
                        item-text="name"
                        item-value="id"
                        :return-object="false"
                        :label="$t('inputs.specialty')"
                        dense
                        clearable
                        hide-details
                        :loading="filterLoading"
                        :disabled="loading"
                        outlined
                        multiple
                    >
                        <template v-slot:selection="{ item, index }">
                            <span v-if="index === 0" class="me-1">{{ item.name }}</span>
                            <span
                                v-if="index === 1"
                                class="grey--text text-caption"
                            >
                                (+{{ specialtyIds.length - 1 }})
                            </span>
                        </template>
                    </v-autocomplete>
                </v-col>
                
                <v-col  md="2" sm="6" cols="12" class="pa-1">
                    <v-autocomplete
                        v-model="medicalRepId"
                        :items="regionIds.length === 0 ? medicalRepsBrief.filter(c => c.employeeType === userType.medicalRep) : medicalRepsForRegion.filter(c => c.employeeType === userType.medicalRep)"
                        item-text="name"
                        item-value="id"
                        :label="$t('inputs.medical-rep')"
                        prepend-icon="mdi-account-tie"
                        hide-details
                        clearable
                        :loading="filterLoading || medicalrepLoading"
                        :disabled="loading || medicalrepLoading"
                        outlined
                        dense
                    ></v-autocomplete>
                </v-col>

                <v-col cols="auto"  class="pa-1 d-flex" align-self="center">
                    <v-btn-toggle
                        v-model="sort"
                        mandatory
                        dense
                        color="primary"
                        class="mb-2"
                        @change="isFiltered = true; needToResetShownRows = true; fetchVisitCount()"
                    >
                        <v-btn :disabled="loading" class="text-none">
                           {{ $t('VisitedDoctor.sort-by-most-visited') }}
                        </v-btn>

                        <v-btn :disabled="loading" class="text-none">
                           {{ $t('VisitedDoctor.sort-by-least-visits') }}
                        </v-btn>
                    </v-btn-toggle>
                </v-col>

                <!-- <v-spacer /> -->

                <v-col cols="auto" align-self="center" class="pt-0">
                    <v-btn
                        color="success"
                        class="px-sm-4"
                        :loading="loading"
                        :disabled="isAllFieldsEmpty || loading"
                        type="submit"
                        small
                    >
                        <v-icon> mdi-magnify </v-icon>
                        {{$t('search')}}
                    </v-btn>
                </v-col>

                <v-col cols="auto" class="d-flex justify-end px-0 pt-0">
					<v-btn
						class="px-sm-8"
						@click="clearFilter(); needToResetShownRows = true; fetchVisitCount();"
						:disabled="!isFiltered || loading"
						color="alert-color white--text"
						small
					>
						{{$t('reset')}}
					</v-btn>
					<v-divider vertical class="mx-2" />
					<v-btn
						color="grey darken-2 white--text"
						small
						:to="{ name: 'visits' }"
					>
                		<v-icon small class="me-1"> mdi-arrow-left-circle </v-icon>
                		{{$t('back')}}
            		</v-btn>
                </v-col>
            </v-row>

            <!-- <v-row class="mb-1 mx-2">
            </v-row> -->
        </v-form>

        <!-- table -->
        <bee-handy-table
            ref="table"
            v-model="tableInputs"
            :headers="headers"
            :items="!loading ? visitedDoctors : []"
            :loading="loading"
            fixed-header
            :height="$vuetify.breakpoint.height - 190"
            :data-table-props="{
                disableSort: true
            }"
            dense
            zebra
            pagination-on-scroll
            :pagination-on-scroll-auto-reset-shown="false"
            pagination-on-scroll-server-items
            :items-per-page="20"
            :scroll-options.sync="optionsScroll"
            :server-items-length="serverItemsLength"
            @pagination-on-scroll:reach-last-row="fetchVisitCount"
        >
            <!-- visits count -->
            <template v-slot:item.visitsCount="{ item }">
                <span :class="{ 'error--text text--lighten-1': item.visitsCount === 0 }">
                    {{item.visitsCount}}
                </span>
            </template>

            <!-- employee -->
            <!-- <template v-slot:item.visitEmployeeIds="{ item }">
                <v-chip
                    v-for="item in item.visitEmployeeIds"
                    :key="item.employeeId"
                	color="primary"
                    class="me-1 mb-1 px-2"
                	small
                >
                    {{`${dig('getMedicalRepById', item.employeeId, 'name')} (${item.count})`}}
                </v-chip>
                <template v-if="!item.visitEmployeeIds.length">
                    -
                </template>
            </template> -->

            <!-- actions -->
            <template v-slot:item.actions="{ item }">
                <tooltip text="Visit details" top>
                    <v-btn
                        v-if="0"
                        icon
                        small
                        color="light-success"
                        :loading="item.id === entityId"
                        :disabled="true || loading || item.id === entityId"
                        @click="entityId = item.id; goToMatchedVisit(item.id)"
                    >
                        <v-icon>mdi-eye-outline</v-icon>
                    </v-btn>
                </tooltip>
            </template>
        </bee-handy-table>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Tooltip from '@/components/Tooltip'
import { dig, findClassification, disableDates } from '@/helpers/functions';
import { managementHierarchyRegions, isDisabledFriday } from '@/configs/global.config'
import moment from 'moment';
import { userType } from '@/helpers/enums'
export default {
    name: 'VisitedDoctors',
    
    components: {
        Tooltip
    },

    data(){
        return {
            visitedDoctors: [],
            searchCityId: null,
            searchRegion: null,
            medicalrepLoading: false,
            
            // filter data
            doctorName: null,
            cityId: [],
            regionIds: [],
            workload: [],
            specialtyIds: [],
            startDate: moment(moment().startOf('month')).format('YYYY-MM-DD'),
            endDate: moment(moment().endOf('month')).format('YYYY-MM-DD'),

            // helpers
            loading: false,
            filterLoading: false,
            regionsLoading: false,
            isFiltered: false,
            tableInputs: {},
            entityId: null,
            userType,

            // pagination
            needToResetShownRows: false,
            optionsScroll: {},
            serverItemsLength: 0,
            medicalRepId: null,
            managementHierarchyRegions,
            sort: null,
            isDisabledFriday,
            disableDates,
            medicalRepsForRegion: []
        }
    },

    computed: {
        ...mapState({
            cities: state => state.cities.cities,
            regions: state => state.regions.regions,
            medicalRepsBrief: state => state.medicalreps.medicalRepsBrief,
            specialties: state => state.specialties.specialties,
            classification: state => state.workload.classification,
            regionsAll: state => state.regions.regionsAll
        }),
        
        ...mapGetters({
            getMedicalRepById: 'medicalreps/getMedicalRepById',
            getSpecialtyById: 'specialties/getSpecialtyById',
            getEntityById: 'entities/getEntityById',
            getClassificationById: 'workload/getClassificationById',
            getRegionAllById: 'regions/getRegionAllById'
        }),

        isAllFieldsEmpty(){
            return (
                !this.doctorName && this.regionIds.length === 0 && !this.startDate && !this.endDate && !this.selectedType && this.specialtyIds.length === 0 && this.workload.length === 0 && !this.medicalRepId
            )
        },

        // selection helpers
        icon() {
            if (this.selectedAllRegions) return 'mdi-close-box'
            return 'mdi-checkbox-blank-outline'
        },
      
        selectedAllRegions () {
            return this.regionIds.length === this.regionsAll.filter(c => this.cityId?.includes(c.cityId)).length;
        },
        headers() {
            const headers = [
                { 
                    text: this.$t('headers.doctor'), 
                    name: 'doctorName',
                    align: 'start', 
                    value: 'name',
                    columnClass: 'primary--text',
                },
                {
                    text: this.$t('headers.specialty'),
                    name: 'specialty',
                    value:'specialtyIds',
                    setValueExpr: val => val.map(c => this.getSpecialtyById(c)?.name).join(', '),
                },
                {
                    text: this.$t('headers.class'),
                    name: 'classification',
                    value:'entityAddresses',
                    setValueExpr: val => val.length !== 0 ? this.getClassificationById(val[0].workloadId)?.name : '-'
                },
                {
                    text: !managementHierarchyRegions ? this.$t('headers.region') : this.$t('headers.area'), 
                    name: 'regionId',
                    value: 'entityAddresses',
                    setValueExpr: val => val.length !== 0 ? this.getRegionAllById(val[0].regionId)?.name : '-',
                    noNotAvailableText: true,
                },
                { 
                    text: this.$t('headers.visits-count'), 
                    name: 'visitsCount', 
                    value: 'visitsCount', 
                    align: 'center',                     
                    type: 'number',
                    noNotAvailableText: true
                },
                // { 
                //     text: 'Medical Reps', 
                //     name: 'visitEmployeeIds',
                //     value: 'visitEmployeeIds', 
                //     setValueExpr: val => val ? val.map(c => this.getMedicalRepById(c.employeeId))?.name : null,
                //     align: 'center',
                //     width: 400,
                // },

                // {
                //     text: 'Date of last visit',
                //     name: 'date',
                //     value: '-',
                // }
                // {
                //     text: 'Actions',
                //     name: 'actions',
                //     value: 'actions'
                // }
            ];

            const sharedClass = 'text-body-2 font-weight-bold px-2';

            for (let i = 0; i < headers.length; i++) {
                headers[i].class = headers[i].class ? headers[i].class + ' ' + sharedClass : sharedClass;                
                headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-2' : 'px-2';                
            }

            return headers
        },

        iconCities () {
            if (this.selectedAllCities) return 'mdi-close-box'
            return 'mdi-checkbox-blank-outline'
        },

        selectedAllCities () {
            return this.cityId.length === this.cities.length;
        },
    },

    methods: {
        // helpers
        dig,
        moment,
        goToMatchedVisit(id) {
            this.$store.dispatch('entities/getById', { id }).then((data) => {
                const queryData = {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    cityId: this.cityId,
                    regionIds: this.regionIds,
                    specialty: this.specialtyIds,
                    workloadId: this.workload,
                    doctorName: data.name
                }
                const query = {};
                Object.keys(queryData).forEach(key => {
                    if (queryData[key]) query[key] = queryData[key];
                })

                this.$router.push({ name: 'visits', query });
            }).finally(() => {
                this.entityId = null;
            })
        },
        
        fetchRegionsDueCity(){
            this.regionIds = [];
            if (!this.cityId) return;
            this.regionsAll.forEach(region => {
                if (this.cityId.includes(region.cityId)) {
                    this.regionIds.push(region.id);
                }
            });

            if (this.regionIds.length) {
                this.medicalrepLoading = true
                this.$store.dispatch('medicalreps/fetchAllBrief', { regionIds: this.regionIds }).then((data) => {
                    this.medicalRepsForRegion = data
                }).finally(() => {
                    this.medicalrepLoading = false
                })
            }
        },

        // needed functions for filter
        clearFilter(){
            this.doctorName = null;
            this.cityId = [];
            this.regionIds = [];
            this.selectedType = null;
            this.specialtyIds = []
            this.workload = []
            this.visit = null;
            this.startDate = moment(moment().startOf('month')).format('YYYY-MM-DD');
            this.endDate = moment(moment().endOf('month')).format('YYYY-MM-DD')
            this.medicalRepId = null

            // reset total visits
            this.serverItemsLength = 0;

            this.isFiltered = false;
        },

        // select all regions function
        toggle () {
            this.$nextTick(() => {
                if (this.selectedAllRegions) {
                    this.regionIds = []
                } 
                else {
                    this.regionIds = []
                    this.regionIds = this.regionsAll.filter(c => this.cityId.includes(c.cityId)).map(c => c.id);
                }
            })
        },

        fetchVisitCount(newOptionsScroll) {
            this.loading = true;
            if (this.isFiltered) this.filterLoading = true;
            
            let page = 1;

            if (newOptionsScroll) {
                page = newOptionsScroll.page;
            }

            const { itemsPerPage } = this.optionsScroll;

            return this.$store.dispatch('entities/fetchVisitedDoctorsByFilter', {
                name: this.doctorName,
                workloadIds: this.workload,
                // specialtyIds: this.specialtyIds,
                SpecialtyIds: this.specialtyIds,
                regionIds: this.regionIds,
                withVisitsCountFrom: this.startDate,
                WithVisitsCountTo: this.endDate,
                IncludeAddresses: true,
                EmployeeId: this.medicalRepId,
                page: page,
                perPage: itemsPerPage || 20,
                VisitsCountDescending: this.sort !== null ? Boolean(!this.sort) : null,
                orderByWorkLoad: (this.sort === null) ? true : null,
            })
            .then(({ data, total }) => {
                if (this.needToResetShownRows) {
                    this.$refs.table.resetShownRows();
                    this.needToResetShownRows = false;
                }
                this.$refs.table.addScrollItems(data);
                this.serverItemsLength = total;
            })
            .finally(() => {
                this.loading = false;
                if (this.isFiltered) this.filterLoading = false;
            })
        },

        toggleCities () {
            this.$nextTick(() => {
                if (this.selectedAllCities) {
                    this.cityId = []
                    this.regionIds = []
                } 
                else {
                    this.cityId = this.cities.map(c => c.id);
                    this.fetchRegionsDueCity()
                }
            })
        },

        getMedical() {
            if (this.regionIds.length) {
                this.medicalrepLoading = true
                this.medicalRepId = null
                this.$store.dispatch('medicalreps/fetchAllBrief', { regionIds: this.regionIds }).then((data) => {
                    this.medicalRepsForRegion = data
                }).finally(() => {
                    this.medicalrepLoading = false
                })
            }
        },
    },

    created(){
        this.loading = true;
        if (!this.medicalRepsBrief.length) {
            this.medicalrepLoading = true
            this.$store.dispatch('medicalreps/fetchAllBrief', true).finally(() => {
                this.medicalrepLoading = false
            })
        }

        Promise.all([
            !this.classification.length ? this.$store.dispatch('workload/fetchAll') : null,
            !this.cities.length ? this.$store.dispatch('cities/fetchCityMine', {}).then((data) => {
                this.$store.dispatch('regions/fetchAllRegions', { cities: data })
            }) : null,
            !this.specialties.length ? this.$store.dispatch('specialties/fetchAll') : null,
            this.fetchVisitCount()
        ])
        .finally(() => {
            this.loading = false;
        });
    },
}
</script>